import React, { useContext } from "react";
import { Route, Routes } from "react-router-dom";
import { getRoutes } from "../../../config/ComponentRegister";
import Auth from "../AuthCheck";
import { ConfigProvider } from '../../../context/ConfigContext';
import AuthContext from "../AuthContext";
import useMyPermissions from "../../permissions/useMyPermissions";
import { appConfigs, detectAppFromPath } from "../../../components/common/appRoutes";

const RouteManager = () => {
  const routes = getRoutes();
  const { permissions, user } = useContext(AuthContext);
  const { canAccessPage, isConfigured: hasPermissionGroupConfigured } = useMyPermissions();


  const hasPermission = (requiredPerm, path) => {
    if (!requiredPerm) return true;
    if (permissions.includes("all")) return true;

    const legacyAllowed = Array.isArray(requiredPerm)
      ? requiredPerm.some(p => permissions.includes(p))
      : permissions.includes(requiredPerm);

    if (legacyAllowed) return true;

    // Permission Groups override: the legacy PERM_* codes above are often
    // stale (role defaults, never revisited), so a route that's missing one
    // shouldn't stay unreachable forever just because of that. Once a
    // member has been placed in a Permission Group, let that group decide
    // instead - if it grants view access to this page (or the whole app
    // it belongs to), register the route even though the old code is
    // absent. See useMyPermissions.canAccessPage for the exact rule.
    if (hasPermissionGroupConfigured && path) {
      const appKey = detectAppFromPath(path);
      const permissionModule = appConfigs[appKey]?.permissionModule;
      if (permissionModule) {
        return canAccessPage(permissionModule, path, "view");
      }
    }

    return false;
  };

  const hasRoleAccess = (route) => {
    if (!route.restrictedRoles) return true;
    const userRole = user?.role_name || '';
    return !route.restrictedRoles.includes(userRole);
  };

  return (
    <ConfigProvider>
      <Routes>
        {routes.map((route, index) => {
          const { path, component: Component, auth, permissions: requiredPerm } = route;
          if (!Component || typeof Component !== "function") {
            return null;
          }

          if (!hasPermission(requiredPerm, path) || !hasRoleAccess(route)) {
            return null;
          }

          const WrappedComponent = () => {
            try {
              return <Component />;
            } catch (error) {
              console.error('Component error:', error);
              return (
                <div style={{ padding: '20px', color: 'red' }}>
                  <h3>Error loading component</h3>
                  <pre>{error.message}</pre>
                </div>
              );
            }
          };

          return (
            <Route
              key={index}
              path={path}
              element={
                <Auth check={auth}>
                  <WrappedComponent />
                </Auth>
              }
            />
          );
        })}
      </Routes>
    </ConfigProvider>
  );
};

export default RouteManager;
